/**
 * useProducts - TanStack Query Hooks
 *
 * Hooks pour la gestion des produits, catégories et taux TVA
 *
 * Pattern: Component → Hook (TanStack Query) → Service (API)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsAPI, ProductCreate } from '@/utils/productsAPI';
import { categoriesAPI } from '@/utils/categoriesAPI';
import type { Product, ProductCategory, TaxRate } from '@/types/models';
import { logger } from '@/utils/logger';
import { getErrorMessage } from '@/lib/errorHandler';
import { notifications } from '@/lib/notifications';

// ==================== QUERY KEYS ====================

export const productsKeys = {
  all: ['products'] as const,
  lists: () => [...productsKeys.all, 'list'] as const,
  list: (filters: ProductFilters) => [...productsKeys.lists(), filters] as const,
  detail: (id: string) => [...productsKeys.all, 'detail', id] as const,
};

export const categoriesKeys = {
  all: ['product-categories'] as const,
  list: () => [...categoriesKeys.all, 'list'] as const,
};

export const taxRatesKeys = {
  all: ['tax-rates'] as const,
  list: () => [...taxRatesKeys.all, 'list'] as const,
};

// ==================== TYPES ====================

export interface ProductFilters {
  categoryId?: string;
  active?: boolean;
  search?: string;
}

// ==================== QUERIES ====================

/**
 * Liste des produits avec filtres
 */
export function useProducts(filters: ProductFilters = {}) {
  return useQuery<Product[]>({
    queryKey: productsKeys.list(filters),
    queryFn: async () => {
      const response = await productsAPI.getAll({
        categoryId: filters.categoryId || undefined,
        active: filters.active,
        search: filters.search || undefined,
      });
      // Backend returns { success: true, data: products[], meta: {...} }
      return response.data || response.products || [];
    },
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Liste des catégories de produits
 */
export function useProductCategories() {
  return useQuery<ProductCategory[]>({
    queryKey: categoriesKeys.list(),
    queryFn: async () => {
      const data = await categoriesAPI.getAll();
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - categories change rarely
  });
}

/**
 * Liste des taux TVA
 */
export function useTaxRates() {
  return useQuery<TaxRate[]>({
    queryKey: taxRatesKeys.list(),
    queryFn: async () => {
      const rates = await productsAPI.getTVARates();
      return rates;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - tax rates rarely change
  });
}

// ==================== MUTATIONS ====================

/**
 * Créer un produit
 */
export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productData: ProductCreate) => {
      return await productsAPI.create(productData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productsKeys.all });
    },
    onError: (error) => {
      logger.error('Failed to create product:', error);
      notifications.error(getErrorMessage(error));
    },
  });
}

/**
 * Mettre à jour un produit
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ProductCreate }) => {
      return await productsAPI.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productsKeys.all });
    },
    onError: (error) => {
      logger.error('Failed to update product:', error);
      notifications.error(getErrorMessage(error));
    },
  });
}

/**
 * Supprimer un produit
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productId: string) => {
      return await productsAPI.delete(productId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productsKeys.all });
    },
    onError: (error) => {
      logger.error('Failed to delete product:', error);
      notifications.error(getErrorMessage(error));
    },
  });
}
